<template>
    <div class="rounded-xl border border-gray-200 bg-white p-6 shadow-sm h-full flex flex-col">
        <div class="flex items-start justify-between">
            <div>
                <div
                    class="inline-flex items-center px-3 py-1 rounded-full bg-indigo-100 text-indigo-800 text-xs font-semibold">
                    🤝 Engagement
                </div>
                <h3 class="mt-4 text-2xl font-extrabold text-indigo-900">
                    Academic & Industry Engagements
                </h3>
                <p class="mt-2 text-gray-600">
                    Activities coordinated by {{ userName || 'this user' }} to build partnerships and impact.
                </p>
            </div>
        </div>

        <!-- Loading -->
        <div v-if="isLoading && items.length === 0" class="flex-1 grid place-items-center">
            <div class="flex items-center space-x-3 text-gray-500">
                <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span class="text-sm">Loading Engagements...</span>
            </div>
        </div>

        <!-- Empty -->
        <div v-else-if="items.length === 0" class="flex-1 grid place-items-center">
            <div class="text-center">
                <div
                    class="w-24 h-24 mx-auto rounded-full bg-indigo-50 border border-indigo-100 grid place-items-center">
                    <span class="text-3xl">🤝</span>
                </div>
                <h3 class="mt-5 text-lg font-semibold text-indigo-900">No Engagements yet</h3>
                <p class="mt-1 text-sm text-gray-600">Engagements created by {{ userName || 'this user' }} will appear
                    here.</p>
                <div class="mt-4">
                    <button @click="goToEngagements"
                        class="px-4 py-2 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-sm transition">Create
                        Engagement</button>
                </div>
            </div>
        </div>

        <!-- Metrics + List -->
        <div v-else class="mt-6 flex flex-col">
            <!-- Tiles -->
            <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div class="rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <p class="text-sm text-gray-500">Total Engagements</p>
                    <p class="mt-1 text-3xl font-extrabold text-indigo-900">{{ metricEngagements }}</p>
                </div>
                <div class="rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <p class="text-sm text-gray-500">Total Attachments</p>
                    <p class="mt-1 text-3xl font-extrabold text-indigo-900">{{ metricAttachments }}</p>
                </div>
                <div class="rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <p class="text-sm text-gray-500">Latest Activity</p>
                    <p class="mt-1 text-3xl font-extrabold text-indigo-900">{{ latestYear }}</p>
                </div>
            </div>

            <!-- List -->
            <div class="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div v-for="item in items" :key="item.token"
                    class="rounded-xl border border-gray-200 bg-white hover:shadow-md transition cursor-pointer flex flex-col overflow-hidden">
                    <div class="h-32 bg-indigo-50 border-b border-gray-100 grid place-items-center">
                        <span class="text-4xl">🎯</span>
                    </div>
                    <div class="p-4 flex-1 flex flex-col">
                        <div class="flex items-start justify-between">
                            <span class="text-sm font-semibold text-gray-700 truncate w-full">{{ item.title ||
                                'Untitled'
                            }}</span>
                            <span class="text-xs text-gray-400 whitespace-nowrap">{{ formatDate(item.created_at
                                ||
                                item.created_at) }}</span>
                        </div>
                        <div class="mt-3 text-sm text-gray-600 flex-1 overflow-hidden">
                            <p class="leading-relaxed">{{ truncate(item.description, 150) }}</p>
                        </div>
                        <div class="mt-3 flex items-center justify-between text-xs">
                            <span class="text-gray-500">{{ item.engagement_type || '—' }}</span>
                            <button @click.stop="openEngagement()"
                                class="px-3 py-1 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium transition">
                                Open
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Loading more -->
        <div v-if="isLoading && items.length > 0" class="text-center py-4 text-gray-500">Loading more...</div>
        <div v-if="!hasMore && items.length > 0" class="text-center py-4 text-gray-400">No more items</div>
    </div>
</template>

<script>
import gql from 'graphql-tag';

const LIST_USER_ENGAGEMENTS = gql`
  query ListUserEngagements($userToken: String, $first: Int!, $page: Int!) {
    listUserEngagements(userToken: $userToken, first: $first, page: $page) {
      error
      error_code
      message
      paginatorInfo { hasMorePages currentPage total }
      engagements_total
      attachments_total
      data {
        token
        title
        description
        engagement_type
        date_of_engagement
        created_at
      }
    }
  }
`;

export default {
    name: "Engagement",
    props: {
        token: { type: String, default: "" },
        userName: { type: String, default: "" }
    },
    data() {
        return {
            items: [],
            isLoading: false,
            first: 6,
            page: 1,
            hasMore: true,
            metricEngagements: 0,
            metricAttachments: 0,
        };
    },
    computed: {
        latestYear() {
            if (!this.items.length) return '—';
            const dates = this.items.map(i => new Date(i.date_of_engagement || i.created_at)).filter(d => !isNaN(d));
            if (!dates.length) return '—';
            return Math.max.apply(null, dates.map(d => d.getFullYear()));
        }
    },
    mounted() {
        this.fetchEngagements();
        this.$nextTick(() => {
            const el = document.getElementById('dashboard-scroll-container');
            if (el) el.addEventListener('scroll', this.handleScroll);
        });
    },
    beforeUnmount() {
        const el = document.getElementById('dashboard-scroll-container');
        if (el) el.removeEventListener('scroll', this.handleScroll);
    },
    methods: {
        async fetchEngagements() {
            if (this.isLoading || !this.hasMore) return;
            this.isLoading = true;
            try {
                const { data } = await this.$apollo.query({
                    query: LIST_USER_ENGAGEMENTS,
                    variables: { userToken: this.token || null, first: this.first, page: this.page },
                    fetchPolicy: 'network-only'
                });
                const payload = data && data.listUserEngagements;
                if (payload && !payload.error) {
                    const list = payload.data || [];
                    if (this.page === 1) this.items = list; else this.items = this.items.concat(list);
                    this.hasMore = payload.paginatorInfo && payload.paginatorInfo.hasMorePages;
                    this.metricEngagements = (payload.paginatorInfo && payload.paginatorInfo.total) || this.metricEngagements;
                    this.metricAttachments = typeof payload.attachments_total === 'number' ? payload.attachments_total : this.metricAttachments;
                }
            } finally {
                this.isLoading = false;
            }
        },
        handleScroll(e) {
            const el = e.target;
            const bottomGap = el.scrollHeight - el.scrollTop - el.clientHeight;
            if (bottomGap < 200 && this.hasMore && !this.isLoading) {
                this.page += 1;
                this.fetchEngagements();
            }
        },
        openEngagement() {
            this.$router.push('/academic-industry-engagement-hub');
        },
        goToEngagements() {
            this.$router.push('/academic-industry-engagement-hub');
        },
        truncate(text, max) {
            if (!text) return '';
            const str = String(text);
            if (str.length <= max) return str;
            return str.slice(0, max).trimEnd() + '…';
        },
        formatDate(val) {
            if (!val) return '';
            try { return new Date(val).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); } catch (e) { return ''; }
        },
    }
}
</script>

<style lang="scss" scoped></style>
